import 'src/global.css';

import 'dayjs/locale/pt-br';

import { useEffect } from 'react';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { usePathname } from 'src/routes/hooks';

import { themeConfig, ThemeProvider } from 'src/theme';
import { TxModalProvider } from 'src/auditoria/components/tx-simple-modal/tx-modal-provider';

import { Snackbar } from 'src/components/snackbar';
import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import { defaultSettings, SettingsProvider } from 'src/components/settings';

import { AuthProvider } from 'src/auth/auth-context';

import { GestorModeProvider } from './auditoria/contexts/GestorModeContext';
import { EditPermissionsProvider } from './auditoria/contexts/EditPermissionsContext';
import { AlertProvider } from './auditoria/components/tx-alert-dialog/tx-alert-dialog';

// ----------------------------------------------------------------------
//
// Árvore de providers MÍNIMA do Portal (scaffold). Sem i18n, sem
// auth-multiprovider, sem checkout/settings-drawer do Auditoria.
// GestorMode/EditPermissions são stubs necessários pelo nav-section.
// ----------------------------------------------------------------------

type AppProps = {
  children: React.ReactNode;
};

export default function App({ children }: AppProps) {
  useScrollToTop();

  return (
    <SettingsProvider defaultSettings={defaultSettings}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        <ThemeProvider
          modeStorageKey={themeConfig.modeStorageKey}
          defaultMode={themeConfig.enableSystemMode ? 'system' : themeConfig.defaultMode}
        >
          <MotionLazy>
            <Snackbar />
            <ProgressBar />
            <TxModalProvider>
              <AlertProvider>
                <GestorModeProvider>
                  <EditPermissionsProvider>
                    <AuthProvider>{children}</AuthProvider>
                  </EditPermissionsProvider>
                </GestorModeProvider>
              </AlertProvider>
            </TxModalProvider>
          </MotionLazy>
        </ThemeProvider>
      </LocalizationProvider>
    </SettingsProvider>
  );
}

// ----------------------------------------------------------------------

function useScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
